import { Injectable, OnDestroy } from '@angular/core';
import { BehaviorSubject, Observable, Subscription, interval } from 'rxjs';
import { PROJECT_IDLE_TIMEOUT_MS } from '../utilities/project-access-area';

@Injectable()
export class TimerService implements OnDestroy {
    private timerSubscription: Subscription | null = null;
    private currentProjectId: number | null = null;
    private elapsedSeconds = 0;
    private timerSubject = new BehaviorSubject<string>('00:00:00');

    public timer$: Observable<string> = this.timerSubject.asObservable();
    private isRunning = false;
    private idleTimer: any;
    private idleCallback: (() => void) | null = null;
    private idleEvents = ['mousemove', 'keydown', 'click', 'scroll'];
    private idleListener = () => this.resetIdleTimer();
    private idleTimeout = PROJECT_IDLE_TIMEOUT_MS;
    private idleTriggered = false;
    private lastActivityAt = new Date();

    constructor() { }

    /**
     * Starts the timer. If a projectId is provided and it differs from the current one, the timer resets.
     * If the projectId is the same, it resumes from the current elapsed time.
     */
    startTimer(projectId?: number): void {
        if (projectId !== undefined) {
            if (this.currentProjectId !== projectId) {
                this.elapsedSeconds = 0;
                this.currentProjectId = projectId;
            }
        } else {
            // If no projectId is passed, we reset to 0 by default (backward compatibility)
            this.elapsedSeconds = 0;
        }

        if (this.isRunning) {
            console.warn('Timer is already running');
            return;
        }

        this.isRunning = true;
        this.updateTimerDisplay();

        // Update every second
        this.timerSubscription = interval(1000).subscribe(() => {
            this.elapsedSeconds++;
            this.updateTimerDisplay();
        });

        console.log(`Timer started/resumed for project: ${this.currentProjectId}`);
    }

    /**
     * Stops the timer and returns the elapsed time in seconds
     */
    stopTimer(): number {
        if (!this.isRunning) {
            console.warn('Timer is not running');
            return 0;
        }

        if (this.timerSubscription) {
            this.timerSubscription.unsubscribe();
            this.timerSubscription = null;
        }

        this.isRunning = false;
        const elapsed = this.elapsedSeconds;

        console.log(`Timer stopped. Elapsed time: ${this.formatTime(elapsed)}`);

        return elapsed;
    }

    /**
     * Resets the timer to 00:00:00
     */
    resetTimer(): void {
        this.stopTimer();
        this.elapsedSeconds = 0;
        this.currentProjectId = null;
        this.updateTimerDisplay();
    }

    /**
     * Gets the current elapsed time in seconds
     */
    getElapsedSeconds(): number {
        return this.elapsedSeconds;
    }

    /**
     * Checks if timer is currently running
     */
    isTimerRunning(): boolean {
        return this.isRunning;
    }

    /**
     * Updates the timer display
     */
    private updateTimerDisplay(): void {
        this.timerSubject.next(this.formatTime(this.elapsedSeconds));
    }

    /**
     * Formats seconds into HH:MM:SS format
     */
    private formatTime(totalSeconds: number): string {
        const hours = Math.floor(totalSeconds / 3600);
        const minutes = Math.floor((totalSeconds % 3600) / 60);
        const seconds = totalSeconds % 60;

        return `${this.padZero(hours)}:${this.padZero(minutes)}:${this.padZero(seconds)}`;
    }

    /**
     * Pads single digit numbers with leading zero
     */
    private padZero(num: number): string {
        return num.toString().padStart(2, '0');
    }

    /**
     * True once the current idle monitor has fired, until the next one starts.
     * Lets a view that is torn down by the idle navigation - rather than by the
     * user - tell the server to discount the idle window.
     */
    get wasIdleTriggered(): boolean {
        return this.idleTriggered;
    }

    /**
     * When the user last did anything on the page. Sent as the visit's TimeOut
     * so an idle exit records the time actually spent rather than having a flat
     * idle window guessed off it server side.
     */
    get lastActivity(): Date {
        return this.lastActivityAt;
    }

    startIdleMonitor(timeoutMs: number, onIdle: () => void) {
        this.stopIdleMonitor();
        this.idleTriggered = false;
        this.idleTimeout = timeoutMs;
        this.idleCallback = onIdle;

        this.idleEvents.forEach(event => {
            window.addEventListener(event, this.idleListener);
        });

        this.resetIdleTimer();
    }

    stopIdleMonitor() {
        if (this.idleTimer) clearTimeout(this.idleTimer);
        this.idleEvents.forEach(event => {
            window.removeEventListener(event, this.idleListener);
        });
    }

    private resetIdleTimer() {
        this.lastActivityAt = new Date();
        if (this.idleTimer) clearTimeout(this.idleTimer);
        this.idleTimer = setTimeout(() => {
            // Stop monitoring once triggered to avoid repeated calls
            this.stopIdleMonitor();
            this.idleTriggered = true;
            if (this.idleCallback) this.idleCallback();
        }, this.idleTimeout);
    }

    ngOnDestroy(): void {
        this.stopTimer();
        this.stopIdleMonitor();
    }
}
